import { useState, useCallback, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Plus, Search, Trash2, MessageSquare, Sparkles } from "lucide-react"
import { useAiStore } from "@/stores/ai-store"
import { useAiConversations, useAiConversation, useCreateAiConversation, useDeleteAiConversation } from "@/hooks/use-ai-conversations"
import { useAgUiStream } from "@/hooks/use-ag-ui-stream"
import { useAiContext } from "@/hooks/use-ai-context"
import { AiGreeting } from "@/components/ai/ai-greeting"
import { AiFullpageMessages } from "@/components/ai/ai-fullpage-messages"
import { AiComposer } from "@/components/ai/ai-composer"
import { cn } from "@/lib/utils"
import type { MentionEntity } from "@/lib/ag-ui-types"

export function AiChatPage() {
  const { t } = useTranslation()
  const { conversationId: routeConvId } = useParams()
  const navigate = useNavigate()
  const context = useAiContext()

  const {
    activeConversationId,
    setActiveConversation,
    setMode,
    resetStreamContent,
    clearMentions,
    close: closeSidebar,
  } = useAiStore()
  const { startRun, abort } = useAgUiStream()
  const createConversation = useCreateAiConversation()
  const deleteConversation = useDeleteAiConversation()

  useEffect(() => {
    setMode("fullpage")
    closeSidebar()
    return () => setMode("sidebar")
  }, [setMode, closeSidebar])

  useEffect(() => {
    if (routeConvId && routeConvId !== activeConversationId) {
      setActiveConversation(routeConvId)
    }
  }, [routeConvId, activeConversationId, setActiveConversation])

  const { data: conversationsData } = useAiConversations()
  const conversations = conversationsData?.data || []
  const [searchTerm, setSearchTerm] = useState("")

  const filteredConversations = searchTerm
    ? conversations.filter((c) =>
        (c.title || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : conversations

  const { data: conversationData } = useAiConversation(activeConversationId)
  const messages = activeConversationId ? conversationData?.messages || [] : []

  const handleSend = useCallback(
    async (content: string, mentions?: MentionEntity[]) => {
      let convId = activeConversationId

      if (!convId) {
        const result = await createConversation.mutateAsync({
          context_type: context.context_type,
          context_id: context.context_id,
        })
        convId = result.data.id
        setActiveConversation(convId)
        navigate(`/ai/${convId}`, { replace: true })
      }

      startRun({
        conversationId: convId,
        content,
        currentPage: context.current_page,
        contextType: context.context_type,
        contextLabel: context.context_label,
        mentionedEntities: mentions,
      })
    },
    [activeConversationId, context, createConversation, setActiveConversation, navigate, startRun]
  )

  const handleNewChat = () => {
    abort()
    setActiveConversation(null)
    resetStreamContent()
    clearMentions()
    navigate("/ai")
  }

  const handleSelectConversation = (id: string) => {
    setActiveConversation(id)
    navigate(`/ai/${id}`)
  }

  const handleDeleteConversation = (id: string) => {
    if (id === activeConversationId) {
      setActiveConversation(null)
      navigate("/ai")
    }
    deleteConversation.mutate(id)
  }

  const hasMessages = messages.length > 0

  return (
    <div className="-m-4 flex h-[calc(100vh-3rem)] md:-m-5 md:h-[calc(100vh-3rem)]">
      {/* ─── Left panel: conversation history ─── */}
      <div className="hidden w-[240px] flex-shrink-0 flex-col border-r bg-[var(--bg-surface)] md:flex">
        {/* Panel header */}
        <div className="flex h-12 items-center gap-2 border-b px-3">
          <Sparkles size={14} className="text-blue-500" />
          <span className="text-[13px] font-semibold text-foreground">OkeyAI</span>
        </div>

        {/* New chat + search */}
        <div className="px-2.5 pt-2.5 pb-2">
          <button
            onClick={handleNewChat}
            className="flex w-full items-center justify-center gap-1.5 rounded-md bg-[var(--bg-surface-raised)] py-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-surface-overlay)] hover:text-foreground"
          >
            <Plus size={13} />
            {t("ai.new_conversation", "Nowa rozmowa")}
          </button>

          <div className="relative mt-2">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("ai.search_history", "Szukaj...")}
              className="w-full rounded-md bg-transparent py-1 pl-6 pr-2 text-[12px] text-foreground outline-none placeholder:text-[var(--text-tertiary)]"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex flex-1 flex-col overflow-y-auto border-t pt-1">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center px-4">
              <MessageSquare size={18} className="text-[var(--text-tertiary)] opacity-30" />
              <p className="mt-2 text-[11px] text-[var(--text-tertiary)]">Brak rozmów</p>
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const isActive = conv.id === activeConversationId
              return (
                <div
                  key={conv.id}
                  className={cn(
                    "group relative mx-1.5 mb-px rounded-md transition-colors",
                    isActive ? "bg-[var(--sidebar-bg-active)]" : "hover:bg-[var(--sidebar-bg-hover)]"
                  )}
                >
                  <button
                    onClick={() => handleSelectConversation(conv.id)}
                    className="w-full px-2.5 py-1.5 text-left outline-none"
                  >
                    <p
                      className={cn(
                        "truncate text-[13px]",
                        isActive ? "font-medium text-foreground" : "text-[var(--text-secondary)]"
                      )}
                    >
                      {conv.title || "Nowa rozmowa"}
                    </p>
                    <p className="mt-px text-[11px] text-[var(--text-tertiary)]">
                      {conv.message_count} wiad.
                      {conv.last_message_at && <> · {new Date(conv.last_message_at).toLocaleDateString("pl")}</>}
                    </p>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteConversation(conv.id)
                    }}
                    className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-[var(--text-tertiary)] opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-red-950/30"
                    aria-label="Usuń rozmowę"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* ─── Main chat area ─── */}
      <div className="flex flex-1 flex-col bg-[var(--bg)]">
        {hasMessages ? (
          <>
            {/* Title bar */}
            {conversationData?.data && (
              <div className="flex h-12 items-center gap-2 border-b px-6">
                <Sparkles size={13} className="shrink-0 text-[var(--text-tertiary)]" />
                <h2 className="truncate text-[13px] font-medium text-foreground">
                  {conversationData.data.title || "Nowa rozmowa"}
                </h2>
              </div>
            )}
            <AiFullpageMessages messages={messages} />
          </>
        ) : (
          <AiGreeting onSend={(prompt) => handleSend(prompt)} />
        )}

        {/* Composer */}
        <div className="px-4 pb-5 pt-3">
          <div className="mx-auto w-full max-w-[680px]">
            <AiComposer
              onSend={(content, mentions) => handleSend(content, mentions)}
              size="large"
              showContext={false}
              autoFocus
            />
          </div>
        </div>
      </div>
    </div>
  )
}
